import React, { ReactElement } from 'react';

// Components
import ModalV2, { DefaultFooterButtons, LoadingState } from '@/views/components/modals/ModalV2';
import SendInviteQuestionnaire from './components/SendQuestionnaire/SendInviteQuestionnaire';
import SendRefreshQuestionnaire from './components/SendQuestionnaire/SendRefreshQuestionnaire';
import SendReviewQuestionnaire from './components/SendQuestionnaire/SendReviewQuestionnaire';
import { CompleteQuestionnaire } from './components/CompleteQuestionnaire';
import { CreateQuestionnaire } from './components/CreateQuestionnaire';
import { NameRiskProfile } from './components/NameRiskProfile';
import { withErrorBoundary } from '@/views/components/errors';
import { DestructiveModal } from '@capintel/component-lib/destructive-modal';

// Enums
import { OmniRiskProfileStatus, OmniRiskProfileSteps, OmniRiskProfileStepsTitlesKeys } from '@/enums/OmniRiskProfiles';

// Libraries
import { connect, useDispatch, useSelector } from 'react-redux';
import { Dispatch } from '@/redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { useHistory } from 'react-router-dom';

// Types
import Locale from '@/types/locale';
import { FormattedFullOmniRiskProfile } from '@/types/risk-profiles/omni/full-omni-risk-profile';
import { ReduxState } from '@/redux/modules/types';

// Redux
import { goToStep, hideModal, resetQuestionnaire } from '@/redux/modules/omni-risk-profile-modal';
import {
  COMPLETE_QUESTIONNAIRE,
  CREATE_OMNI_RISK_PROFILE,
  DELETE_OMNI_RISK_PROFILE,
  createOmniRiskProfile,
  deleteOmniRiskProfile,
  getOmniRiskProfiles,
  updateOmniRiskProfile,
} from '@/redux/modules/omni-risk-profiles';

// Utils
import notification from '@/utils/notification';
import { DATE_KEYS, sortByDateKey } from '@/utils/dates';
import { formatMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { stopLoaderAction } from '@/redux/modules/ui/loaders';
import { useLoadingUIState } from '@/hooks/useLoadingState';

type ModalStep = {
  title: string;
  body: ReactElement;
  defaultFooterButtons?: DefaultFooterButtons;
  footer?: ReactElement;
  loading?: LoadingState;
};

type ModalSteps = {
  [key in Exclude<OmniRiskProfileSteps, OmniRiskProfileSteps.DELETE_RISK_PROFILE>]: ModalStep;
};

type StateProps = {
  currentRiskProfile: FormattedFullOmniRiskProfile | null;
  currentStep: OmniRiskProfileSteps;
  currentStepTitle: string;
  isNameRiskProfileLoading: boolean;
  isRenaming?: boolean;
  locale: Locale;
  showModal: boolean;
};

type DispatchProps = {
  hideModal: () => void;
  goToStep: (step: OmniRiskProfileSteps, stepTitleKey: OmniRiskProfileStepsTitlesKeys) => void;
  deleteOmniRiskProfile: (riskProfile: FormattedFullOmniRiskProfile) => void;
};

type Props = StateProps & DispatchProps & RouteComponentProps;

const OmniRiskProfileModal: React.FC<Props> = ({
  currentRiskProfile,
  currentStep,
  currentStepTitle,
  isNameRiskProfileLoading,
  isRenaming,
  locale,
  location,
  showModal,
  deleteOmniRiskProfile,
  hideModal,
  goToStep,
}) => {
  const dispatch = useDispatch<Dispatch>();
  const history = useHistory();
  const currentContact = useSelector((state: ReduxState) => state.contacts.current?.contact);
  const user = useSelector((state: ReduxState) => state.auth.user);
  const contactEmail = currentContact?.email;
  const hasEnforcedContactEmail = !!(userHasPermissionEnabled(user, PERMISSIONS.OMNI_RISK_PROFILE_ENFORCE_CONTACT_EMAIL) && contactEmail);
  const isDeleteLoading = useLoadingUIState(DELETE_OMNI_RISK_PROFILE.type);

  const isDeleteStep = currentStep === OmniRiskProfileSteps.DELETE_RISK_PROFILE;

  const handleDeleteRiskProfile = async () => {
    if (currentRiskProfile) {
      await deleteOmniRiskProfile(currentRiskProfile);
    }
  };

  const allowEmailPrefill = (status?: OmniRiskProfileStatus) => {
    if (!status) return false;
    return [OmniRiskProfileStatus.Approved, OmniRiskProfileStatus.ClientReviewUnsent].includes(status);
  };

  const closeModal = async () => {
    hideModal();
    resetQueryParamIfHasMaterialChanges();
    dispatch(resetQuestionnaire());
    dispatch(stopLoaderAction(COMPLETE_QUESTIONNAIRE.type));
    await dispatch(getOmniRiskProfiles());
  };

  const getInitialEmail = (): string => {
    if (hasEnforcedContactEmail) {
      return contactEmail;
    }
    if (currentRiskProfile?.emails?.length && !!allowEmailPrefill(currentRiskProfile?.status)) {
      const latestEmail = currentRiskProfile.emails.sort((a, b) => sortByDateKey(a, b, DATE_KEYS.SENT_AT))[0];
      return latestEmail.email;
    }
    return contactEmail || '';
  };

  const handleNameRiskProfileSubmit = async (name: string) => {
    // The objective of this process is to establish a new questionnaire
    if (!isRenaming) {
      await dispatch(createOmniRiskProfile(name));
      return goToStep(OmniRiskProfileSteps.CREATE_QUESTIONNAIRE, OmniRiskProfileStepsTitlesKeys.CreateQuestionnaire);
    }

    // Renaming a risk profile requires updating the name of the risk profile in the NameRiskProfile component
    if (!currentRiskProfile || !currentRiskProfile.name[locale]) return;

    // The purpose of this flow is to update a questionnaire that already exists
    const newName = { ...currentRiskProfile.name, [locale]: name };
    await dispatch(updateOmniRiskProfile(currentRiskProfile!, { name: newName }));
    closeModal();
    notification.success(
      formatMessage('RiskProfileModal.NameRiskProfile.RenameNotificationBody'),
      formatMessage('RiskProfileModal.NameRiskProfile.RenameNotificationTitle'),
    );
  };

  const modalSteps: ModalSteps = {
    [OmniRiskProfileSteps.NAME_PROFILE]: {
      title: currentStepTitle,
      body: (
        <NameRiskProfile
          closeModal={closeModal}
          existingName={currentRiskProfile?.name[locale]}
          isRenaming={isRenaming}
          onSubmit={handleNameRiskProfileSubmit}
        />
      ),
      loading: {
        isActive: isNameRiskProfileLoading,
        labelToLocalize: 'RiskProfileModal.NameRiskProfile.LoadingLabel',
      },
    },
    [OmniRiskProfileSteps.CREATE_QUESTIONNAIRE]: {
      title: currentStepTitle,
      body: (
        <CreateQuestionnaire
          riskProfile={currentRiskProfile!}
          sendQuestionnaireOnClick={() => {
            if (currentRiskProfile?.status === OmniRiskProfileStatus.Approved) {
              return goToStep(OmniRiskProfileSteps.SEND_REFRESH_QUESTIONNAIRE, OmniRiskProfileStepsTitlesKeys.SendRefreshQuestionnaire);
            }
            goToStep(OmniRiskProfileSteps.SEND_INVITE_QUESTIONNAIRE, OmniRiskProfileStepsTitlesKeys.SendQuestionnaire);
          }}
        />
      ),
    },
    [OmniRiskProfileSteps.SEND_INVITE_QUESTIONNAIRE]: {
      title: currentStepTitle,
      body: <SendInviteQuestionnaire hasEnforcedContactEmail={hasEnforcedContactEmail} initialEmail={getInitialEmail()} riskProfile={currentRiskProfile!} />,
    },
    [OmniRiskProfileSteps.SEND_REFRESH_QUESTIONNAIRE]: {
      title: currentStepTitle,
      body: <SendRefreshQuestionnaire hasEnforcedContactEmail={hasEnforcedContactEmail} initialEmail={getInitialEmail()} riskProfile={currentRiskProfile!} />,
    },
    [OmniRiskProfileSteps.SEND_REVIEW_QUESTIONNAIRE]: {
      title: currentStepTitle,
      body: <SendReviewQuestionnaire hasEnforcedContactEmail={hasEnforcedContactEmail} initialEmail={getInitialEmail()} riskProfile={currentRiskProfile!} />,
    },
    [OmniRiskProfileSteps.COMPLETE_QUESTIONNAIRE]: {
      title: currentStepTitle,
      body: <CompleteQuestionnaire closeModal={closeModal} riskProfile={currentRiskProfile!} />,
    },
  };

  const resetQueryParamIfHasMaterialChanges = () => {
    const searchParams = new URLSearchParams(location.search);
    const SHOWS_SENT_MATERIAL_CHANGES_REVIEW = 'showSendMaterialChangesReview';

    if (searchParams.get(SHOWS_SENT_MATERIAL_CHANGES_REVIEW) === 'true') {
      searchParams.set(SHOWS_SENT_MATERIAL_CHANGES_REVIEW, 'false');
      const basePath = location.pathname;
      history.push(`${basePath}?${searchParams}`);
    }
  };

  if (isDeleteStep) {
    return (
      <DestructiveModal
        open={showModal}
        onOpenChange={open => !open && closeModal()}
        title={formatMessage(currentStepTitle)}
        content={formatMessage('RiskProfileModal.DeleteRiskProfile.Description')}
        confirmButtonText={formatMessage('RiskProfileModal.DeleteRiskProfile.Destructive')}
        cancelButtonText={formatMessage('RiskProfileModal.DeleteRiskProfile.Cancel')}
        onConfirm={handleDeleteRiskProfile}
        onCancel={closeModal}
        loading={isDeleteLoading}
      />
    );
  }

  const step = modalSteps[(currentStep as Exclude<OmniRiskProfileSteps, OmniRiskProfileSteps.DELETE_RISK_PROFILE>) || OmniRiskProfileSteps.NAME_PROFILE];
  const { title, body, defaultFooterButtons, loading } = step;

  return (
    <ModalV2
      body={body}
      defaultFooterButtons={defaultFooterButtons}
      loading={loading}
      modalClassName="omni-risk-profile-modal"
      onClose={() => closeModal()}
      show={showModal}
      title={title}
    />
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  currentRiskProfile: state.omniRiskProfiles.currentRiskProfile,
  currentStep: state.omniRiskProfileModal.currentStep || OmniRiskProfileSteps.NAME_PROFILE,
  currentStepTitle: state.omniRiskProfileModal.currentStepTitle ?? OmniRiskProfileStepsTitlesKeys.NameRiskProfile,
  isNameRiskProfileLoading: !!state.ui.loaders.actions.find(loader => loader.name === CREATE_OMNI_RISK_PROFILE.type),
  isRenaming: state.omniRiskProfileModal.isRenamingQuestionnaire,
  locale: state.intl.locale,
  showModal: state.omniRiskProfileModal.showModal,
});

export default withRouter(
  withErrorBoundary(
    connect(mapStateToProps, {
      deleteOmniRiskProfile,
      goToStep,
      hideModal,
    })(OmniRiskProfileModal),
  ),
);
